import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { take } from 'rxjs';

import { MenuLinkContentMainData, ParagraphCtaData } from '@generated/content';
import { Cta, Footer, MainNav, NavConfigItem } from '@interfaces/config';
import { MenuLinkContentData, SpsMenuLinkContent } from '@interfaces/menu';
import { CmsFixes } from '@interfaces/_fixes';

import { MIN_NAV_ITEM_WEIGHT } from '@core/config';

import { ApiService } from '@services/api/api';
import { CtaComponentResolver } from '@services/resolvers/component-resolvers/cta-component/cta-component-resolver';
import { LanguageService } from '@services/language/language.service';
import { HyphenatePipe } from '../../../pipes/hyphenate/hyphenate.pipe';
import { cloneDeep } from 'lodash-es';
import { addEventProcessor } from '@sentry/angular';

@Injectable({ providedIn: 'root' })
export class MenusResolver {
  // Additional data for menu... most likely CTA
  static includedData: any[];

  constructor(private api: ApiService) {}

  static getMainNavConfig(collection: NavConfigItem[]): NavConfigItem[] {
    const cloned = cloneDeep(collection);
 

    const rootNodeIndex = cloned.findIndex(item => item.id === 'root');
    const root = cloned.splice(rootNodeIndex, 1)[0];

    cloned.forEach(node => {
      const parent: NavConfigItem = node.parent?.id === 'root' ? root : cloned.find(n => n.id === node.parent?.id);

      if (!parent) {
        return;
      }

      if (parent.children.includes(node)) {
        return;
      }

      parent.children.push(node);
    });


    return root.children;
  }

  static getCta(item: MenuLinkContentMainData): Cta.ComponentConfig | null {
    const relationships = item.relationships as CmsFixes.MenuLinkContentMainDataRelationships;

    if (!relationships?.field_cta_mainnav?.data || relationships?.field_cta_mainnav?.data.length === 0) {
      return null;
    }

    const { id } = relationships.field_cta_mainnav.data[0];
    const cta: ParagraphCtaData = this.includedData.find(item => item.id === id);

    if (!cta) {
      return null;
    }

    return CtaComponentResolver.getInstance().resolveComponentConfig(cta)?.config;
  }

  static getSecondCta(item: MenuLinkContentMainData): Cta.ComponentConfig | null {
    const relationships = item.relationships as CmsFixes.MenuLinkContentMainDataRelationships;

    if (!relationships?.field_cta_mainnav?.data || relationships?.field_cta_mainnav?.data.length <= 1) {
      return null;
    }


    const { id } = relationships.field_cta_mainnav.data[1];
    const cta: ParagraphCtaData = this.includedData.find(item => item.id === id);



    if (!cta) {
      return null;
    }

    let test = CtaComponentResolver.getInstance().resolveComponentConfig(cta)?.config;

    return test;
  }

  get selectedLanguage(): string {
    return LanguageService?.selectedLanguage as string;
  }

  public async resolveMainMenu(): Promise<MainNav.LanguageConfig> {
    const collection: MainNav.ConfigItem[] = await this.resolveMenu<MenuLinkContentMainData[]>('main').then(coll => {
    
      MenusResolver.includedData = coll.included || [];
   
      this.injectRootNode(coll.payload);

      return coll.payload
        .filter(item => item.attributes.enabled)
        .map(items => this.mapToItemConfig(items))
        .sort((a, b) => (a.weight < b.weight ? -1 : 1));
    });

    return Promise.resolve({ [this.selectedLanguage]: collection });
  }

  public async resolveFooterMenu(): Promise<Footer.LanguageConfig> {
    const { payload } = await this.resolveMenu<CmsFixes.MenuLinkContentFooterData[]>('footer');
    const freetextSections: string[] = this.resolveFooterSections(payload);

    const links: NavConfigItem[] = payload
      .filter(item => item.attributes.enabled && !item.attributes.field_subfooter_text?.length)
      .map(items => this.mapToItemConfig(items))
      .sort((a, b) => (a.weight < b.weight ? -1 : 1));

    return Promise.resolve({ [this.selectedLanguage]: { links, sections: freetextSections } });
  }

  private async resolveMenu<T extends MenuLinkContentData | MenuLinkContentData[]>(
    menuKey: string
  ): Promise<{ payload: T; included?: any[] }> {
    return new Promise<{ payload: T; included?: any[] }>((resolve, reject) => {
      const params = { ...this.getLanguageFilterParams() };

      if (menuKey === 'main') {
        params['include'] = 'field_cta_mainnav';
      }

      this.api
        .get<SpsMenuLinkContent>(`/views/menu_${menuKey}/default`, params)
        .pipe(take(1))
        .subscribe(({ payload }) =>
          payload
            ? resolve({
                payload: payload.data as T,
                included: payload.included,
              })
            : reject()
        );
    });


  }

  private getLanguageFilterParams(): Params {
    return { 'views-filter[langcode]': this.selectedLanguage.toLowerCase() };
  }

  private getItemParentId(item: MenuLinkContentData = null): string {
    if (item.id === 'root') {
      return null;
    }

    return item.attributes?.parent?.split(':')?.[1] ?? 'root';
  }

  private mapToItemConfig(item: MenuLinkContentData): NavConfigItem {
    const parentId = this.getItemParentId(item);
    const parent = parentId ? { id: parentId, label: null, href: null } : null;
    const path = item.attributes.link.url || item.attributes.link.uri;
  

    const config: NavConfigItem = {
      id: item.id,
      root: item.id === 'root',
      field_is_featured :  item.attributes.field_is_featured,
      label: this.hyphenate(item.attributes.title),
      href: path,
      description: this.hyphenate(item.attributes.description),
      children: [],
      parent,
    
      weight: item.attributes.weight ?? MIN_NAV_ITEM_WEIGHT,
    };

    if (Object.keys(item.relationships || {}).includes('field_cta_mainnav')) {
    
      config.cta = MenusResolver.getCta(item as MenuLinkContentMainData);
      config.secondcta = MenusResolver.getSecondCta(item as MenuLinkContentMainData);
   
     
    }

    return config;
  }

  private injectRootNode(coll: MenuLinkContentMainData[]): void {
    const rootUrl = `/${this.selectedLanguage.toLowerCase()}/home`;
    const rootNode = coll.find(item => item.attributes.link.url.toLowerCase() === rootUrl);

    if (rootNode) {
      return;
    }

    coll.unshift({
      id: 'root',
      type: MenuLinkContentMainData.TypeEnum.MenuLinkContentMain,
      attributes: { title: 'Home', enabled: true, link: { url: rootUrl } },
    });
  }

  private resolveFooterSections(data: CmsFixes.MenuLinkContentFooterData[] = []): string[] {
    const dataItem = data.find(item => !!item.attributes.field_subfooter_text?.length);

    if (!dataItem?.attributes?.field_subfooter_text) {
      return [];
    }

    return dataItem.attributes.field_subfooter_text.map(({ processed }) => processed);
  }

  private hyphenate(string: string): string {
    return HyphenatePipe.transform(string);
  }
}